/* eslint-disable max-lines, max-lines-per-function, complexity -- large adapter; split in a follow-up */
/**
 * Bridge Session data adapter: when Local Bridge API is configured, fetches from it
 * and maps responses to frontend types (BridgeSessionState, EntropyMapResponse, BridgeCardObjective).
 * Used by bridgeSessionService.
 *
 * ISSUE-223 — Drive / product-domain data flows: `productDomainSessionApi` uses `driveFlowAdapter` (main
 * `apiClient` by default; optional `REACT_APP_DRIVE_USE_DEDICATED_LOCAL_BRIDGE`). Local Bridge-only
 * helpers: `fetchSessionStateFromLocalApi`, `localBridgeSessionApi`, `fetchEntropyMapFromLocalApi`.
 */
import type { AxiosInstance } from 'axios';
import { apiClient } from './apiClient';
import { ArtifactService } from './api/artifactService';
import { API_ENDPOINTS } from './api/config';
import { localBridgeApiClient } from './localBridgeApiClient';
import {
  getProductDomainQuestions,
  getProductDomainControlBridge,
  getProductDomainEntropyMap,
  getProductDomainBridgeCard,
  getProductDomainSession,
  listProductDomainSessions,
  getProductDomainFinancialSummary,
  getDemandFunnel,
  getPipeline,
} from './productDomainSessionApi';
import {
  listBridgeSessions,
  getCompany,
  getBridgeSession,
  listMarketFitDeals,
  listMarketFitSituations,
  listMarketFitProducts,
  type LocalBridgeSession,
  type LocalCompany,
} from './localBridgeSessionApi';
import type {
  BridgeSessionState,
  EntropyMapResponse,
  EntropyScore,
} from '../types/bridgeSession';
import type {
  BridgeCardObjective,
  ChangeItem,
  ChangeType,
  AssumptionChange,
  ActionImprovement,
} from '../types/bridgeCard';
import {
  ENTROPY_SUBDOMAINS,
  ENTROPY_FACTOR_TO_SUBDOMAINS,
  ENTROPY_FACTOR_LABELS,
} from '../config/entropyHierarchy';
import { isLocalBridgeApiEnabled } from './localBridgeApiConfig';

/**
 * Fetch session from local API and map to BridgeSessionState.
 */
export async function fetchSessionStateFromLocalApi(
  sessionId: string
): Promise<BridgeSessionState | null> {
  const client = localBridgeApiClient;
  if (!client) return null;
  try {
    await client.get<{
      id: string;
      status: string;
      company_id: string;
      user_id: string;
    }>(`/bridge-sessions/${sessionId}`);
    const questionsRes = await client.get<Array<{ domain: string; factor: string }>>(
      `/bridge-sessions/${sessionId}/questions?phase=current`
    );
    const questions = questionsRes.data || [];
    const domainNamesRaw = questions.map((q) => q.domain);
    const domainNames = domainNamesRaw.filter((name, i, arr) => arr.indexOf(name) === i);
    const domains: BridgeSessionState['domains'] = {};
    for (const name of domainNames) {
      domains[name.toLowerCase()] = {
        artifact_id: `domain-${name}`,
        name,
        state: 'in_progress',
        subdomains: [{ artifact_item_id: `item-${name}`, artifact_id: `artifact-${name}`, name, state: 'in_progress' }],
      };
    }
    const hasResults = await hasSessionResults(client, sessionId);
    return {
      platform_initialized: true,
      control_bridge_id: sessionId,
      domains,
      entropyHeatMap: { state: hasResults ? 'available' : 'locked' },
      bridgeCard: { state: hasResults ? 'complete' : 'incomplete', objective_artifact_item_id: sessionId },
      controlBridge: { state: hasResults ? 'available' : 'locked' },
    };
  } catch {
    return null;
  }
}

async function hasSessionResults(
  client: AxiosInstance,
  sessionId: string
): Promise<boolean> {
  if (!client) return false;
  try {
    await client.get(`/bridge-sessions/${sessionId}/control-bridge`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Fetch Product Domain session state and map to BridgeSessionState.
 */
export async function fetchProductDomainSessionState(
  sessionId: string
): Promise<BridgeSessionState | null> {
  try {
    const questions = await getProductDomainQuestions(sessionId);
    const domainNamesRaw = questions.map((q) => q.domain);
    const domainNames = domainNamesRaw.filter((name, i, arr) => arr.indexOf(name) === i);
    const domains: BridgeSessionState['domains'] = {};
    for (const name of domainNames) {
      domains[name.toLowerCase()] = {
        artifact_id: `domain-${name}`,
        name,
        state: 'in_progress',
        subdomains: [{ artifact_item_id: `item-${name}`, artifact_id: `artifact-${name}`, name, state: 'in_progress' }],
      };
    }
    const hasResults = await hasProductDomainSessionResults(sessionId);
    return {
      platform_initialized: true,
      control_bridge_id: sessionId,
      domains,
      entropyHeatMap: { state: hasResults ? 'available' : 'locked' },
      bridgeCard: { state: hasResults ? 'complete' : 'incomplete', objective_artifact_item_id: sessionId },
      controlBridge: { state: hasResults ? 'available' : 'locked' },
    };
  } catch {
    return null;
  }
}

async function hasProductDomainSessionResults(sessionId: string): Promise<boolean> {
  try {
    await getProductDomainControlBridge(sessionId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Fetch Product Domain entropy map and map to EntropyMapResponse.
 */
export async function fetchProductDomainEntropyMap(
  sessionId: string
): Promise<EntropyMapResponse | null> {
  try {
    const data = await getProductDomainEntropyMap(sessionId);
    if (!data) return null;
    const scores: EntropyScore[] = (data.hierarchy ?? []).flatMap((dg) =>
      (dg.subdomains ?? []).map((s, i) => ({
        artifact_item_id: s.artifact_item_id ?? `item-${s.subdomain_id}-${i}`,
        artifact_id: `artifact-${s.subdomain_id}`,
        artifact_name: s.subdomain_name,
        domain_name: dg.domain_name,
        entropy_score: s.entropy_score ?? 0,
        state: 'ACTIVE',
        calculated_at: new Date().toISOString(),
      }))
    );
    return {
      scores,
      business_entropy_score: data.aggregate_entropy_score,
      highest_entropy_subdomain: data.highest_entropy_subdomain ?? null,
      bridge_focus: data.bridge_focus ?? null,
      marketfit_deep_dive_recommended: data.marketfit_deep_dive_recommended ?? false,
      hierarchy: data.hierarchy ?? [],
      metrics: data.value_ratio != null ? {
        current_revenue: 0,
        current_expense: 0,
        ideal_revenue: 0,
        ideal_expense: 0,
        recoverable_revenue: 0,
        recoverable_expense: 0,
      } : undefined,
      calculated_at: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Fetch entropy data from local API (control-bridge or calculate) and map to EntropyMapResponse
 * using canonical hierarchy: Business → Finance, Operations, Strategy, Revenue, Resources → factors.
 */
export async function fetchEntropyMapFromLocalApi(
  sessionId: string
): Promise<EntropyMapResponse | null> {
  const client = localBridgeApiClient;
  if (!client) return null;
  try {
    let entropy: Array<{ scope: string; entropy: number; correction: number }>;
    let metrics: EntropyMapResponse['metrics'];
    try {
      const controlRes = await client.get<{
        entropy: typeof entropy;
        metrics?: {
          current_revenue: number;
          current_expense: number;
          ideal_revenue: number;
          ideal_expense: number;
          recoverable_revenue: number;
          recoverable_expense: number;
          future_revenue?: number;
          future_expense?: number;
          rem_current?: number;
          eem_current?: number;
        };
      }>(`/bridge-sessions/${sessionId}/control-bridge`);
      entropy = controlRes.data.entropy || [];
      const m = controlRes.data.metrics;
      if (m) {
        metrics = {
          current_revenue: m.current_revenue,
          current_expense: m.current_expense,
          ideal_revenue: m.ideal_revenue,
          ideal_expense: m.ideal_expense,
          recoverable_revenue: m.recoverable_revenue,
          recoverable_expense: m.recoverable_expense,
          future_revenue: m.future_revenue,
          future_expense: m.future_expense,
          rem_current: m.rem_current,
          eem_current: m.eem_current,
        };
      }
    } catch {
      const calcRes = await client.post<{
        entropy?: typeof entropy;
        business?: {
          current: { revenue: number; expense: number };
          ideal: { revenue: number; expense: number };
          recoverable_revenue: number;
          recoverable_expense: number;
        };
        efficiency?: { REM?: { current?: number }; EEM?: { current?: number } };
      }>(`/bridge-sessions/${sessionId}/calculate`);
      const calcData = calcRes.data as Record<string, unknown>;
      entropy = (calcData?.entropy as typeof entropy) || [];
      const biz = calcData?.business as {
        current?: { revenue?: number; expense?: number };
        ideal?: { revenue?: number; expense?: number };
        future?: { revenue?: number; expense?: number };
        recoverable_revenue?: number;
        recoverable_expense?: number;
      } | undefined;
      const eff = calcData?.efficiency as { REM?: { current?: number }; EEM?: { current?: number } } | undefined;
      if (biz?.current && biz?.ideal) {
        metrics = {
          current_revenue: biz.current.revenue ?? 0,
          current_expense: biz.current.expense ?? 0,
          ideal_revenue: biz.ideal.revenue ?? 0,
          ideal_expense: biz.ideal.expense ?? 0,
          recoverable_revenue: biz.recoverable_revenue ?? 0,
          recoverable_expense: biz.recoverable_expense ?? 0,
          future_revenue: biz?.future?.revenue,
          future_expense: biz?.future?.expense,
          rem_current: eff?.REM?.current,
          eem_current: eff?.EEM?.current,
        };
      }
    }
    const scopeMap = new Map<string, { entropy: number; correction: number }>();
    entropy.forEach((e) => scopeMap.set(e.scope, { entropy: e.entropy, correction: e.correction }));

    const businessScore = scopeMap.get('Business')?.entropy;

    const scores: EntropyScore[] = entropy.map((e, i) => ({
      artifact_item_id: `item-${e.scope}-${i}`,
      artifact_id: `artifact-${e.scope}`,
      artifact_name: e.scope,
      domain_name: e.scope,
      entropy_score: e.entropy,
      correction_factor: e.correction,
      state: 'ACTIVE',
      calculated_at: new Date().toISOString(),
    }));

    const hierarchy: EntropyMapResponse['hierarchy'] = ENTROPY_SUBDOMAINS.map((subdomainName) => {
      const factorIds = Object.entries(ENTROPY_FACTOR_TO_SUBDOMAINS)
        .filter(([, subs]) => subs.includes(subdomainName))
        .map(([f]) => f);
      const subdomainItems: EntropyMapResponse['hierarchy'][0]['subdomains'] = [];
      let rollupScore = 0;
      factorIds.forEach((factorId) => {
        const data = scopeMap.get(factorId);
        if (data == null) return;
        const label = ENTROPY_FACTOR_LABELS[factorId] || factorId;
        subdomainItems.push({
          subdomain_id: `factor-${factorId}`,
          subdomain_name: label,
          artifact_item_id: `item-${factorId}`,
          entropy_score: data.entropy,
          correction_factor: data.correction,
        });
        if (data.entropy > rollupScore) rollupScore = data.entropy;
      });
      // Local Bridge API returns per-question-domain scopes (Finance, …) not factor codes (FD, …).
      if (subdomainItems.length === 0) {
        const domainData =
          scopeMap.get(subdomainName) ??
          (subdomainName === 'Resources' ? scopeMap.get('Resource') : undefined);
        if (domainData != null) {
          subdomainItems.push({
            subdomain_id: `domain-${subdomainName}`,
            subdomain_name: subdomainName,
            artifact_item_id: `item-domain-${subdomainName}`,
            entropy_score: domainData.entropy,
            correction_factor: domainData.correction,
          });
          rollupScore = domainData.entropy;
        } else {
          rollupScore = 0;
        }
      }

      return {
        domain_id: `domain-${subdomainName}`,
        domain_name: subdomainName,
        rollup_entropy_score: rollupScore > 0 ? rollupScore : undefined,
        subdomains: subdomainItems,
      };
    });

    return {
      scores,
      business_entropy_score: businessScore,
      hierarchy,
      metrics,
      calculated_at: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/** Session shape from GET /bridge-sessions/:id */
interface LocalSessionForCard {
  bridge_objective?: string;
  current_valuation?: number;
  target_valuation?: number;
  target_months?: number;
  current_revenue?: number;
  current_expense?: number;
  commitment_toll_cost_1hr?: number;
}

/** Question shape from GET /bridge-sessions/:id/questions (with current answer when available) */
interface QuestionForCard {
  question_id: string;
  text: string;
  current_answer?: { score: number } | null;
}

/** Control-bridge metrics (from GET .../control-bridge) */
interface ControlBridgeMetrics {
  value_ratio_current?: number;
  value_ratio_ideal?: number;
  value_ratio_future?: number;
  current_revenue?: number;
  current_expense?: number;
  ideal_revenue?: number;
  ideal_expense?: number;
  recoverable_revenue?: number;
  recoverable_expense?: number;
  future_revenue?: number;
  future_expense?: number;
  rem_current?: number;
  eem_current?: number;
  rem_future?: number;
  eem_future?: number;
}

/** Bridge card item from GET /bridge-sessions/:id/bridge-card (with optional Define Conditions fields) */
interface BridgeCardItemForAdapter {
  question_id: string;
  reason?: string;
  priority: number;
  future_condition?: string | null;
  future_answer_score?: number | null;
  assumption_text?: string | null;
  plank_description?: string | null;
  resource_assignment?: string | null;
  estimated_duration?: string | null;
}

/**
 * Fetch Product Domain bridge card and map to BridgeCardObjective.
 */
export async function fetchProductDomainBridgeCardFromLocalApi(
  sessionId: string
): Promise<BridgeCardObjective | null> {
  try {
    const [session, items, questions, control] = await Promise.all([
      getProductDomainSession(sessionId),
      getProductDomainBridgeCard(sessionId),
      getProductDomainQuestions(sessionId, 'current'),
      getProductDomainControlBridge(sessionId),
    ]);
    if (!session || !items) return null;
    const questionTextById = new Map<string, string>();
    const questionScoreById = new Map<string, number>();
    questions.forEach((q) => {
      if (q.question_id && q.text) questionTextById.set(q.question_id, q.text);
      if (q.current_answer != null) questionScoreById.set(q.question_id, q.current_answer.score);
    });
    const sortedItems = [...items].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    const currentStateItems = sortedItems.map(
      (i) => questionTextById.get(i.question_id) || i.reason || `Address question ${i.question_id}`
    );
    const necessaryConditions = sortedItems.map((i) => ({
      condition: questionTextById.get(i.question_id)
        ? `Address: ${questionTextById.get(i.question_id)}`
        : i.reason || `Address question ${i.question_id}`,
      change_type: undefined as ChangeType | undefined,
    }));
    const entropyList = control?.entropy ?? [];
    const firstEntropy = entropyList[0];
    const entropyScore = typeof firstEntropy?.entropy === 'number' ? firstEntropy.entropy : 7;
    return {
      objective_artifact_item_id: sessionId,
      objective_artifact_id: sessionId,
      target_subdomain_artifact_item_id: sessionId,
      target_subdomain_artifact_id: sessionId,
      target_correction_factor: 1,
      current_correction_factor: entropyScore,
      improvement_objective: session.bridge_objective || 'Reduce product domain entropy',
      id: sessionId,
      artifact_ref: sessionId,
      artifact_name: 'Product Domain',
      artifact_type: 'Product',
      entropy_score_start: entropyScore,
      entropy_score_current: entropyScore,
      current_state: { items: currentStateItems },
      assumptions: [],
      necessary_conditions: necessaryConditions,
      plan: { improvements: [] },
      items_to_change: [] as ChangeItem[],
      status: 'incomplete',
      improvement_artifact_item_ids: [],
    };
  } catch {
    return null;
  }
}

/**
 * Fetch bridge card from local API and map to BridgeCardObjective.
 * When using local API, objectiveArtifactItemId is treated as sessionId.
 * Fetches session (objective, valuations), bridge-card items, questions (for text), and control-bridge (entropy).
 * Maps selected friction questions to current-state high friction priorities and one necessary condition per item.
 */
export async function fetchBridgeCardFromLocalApi(
  objectiveArtifactItemId: string
): Promise<BridgeCardObjective | null> {
  const client = localBridgeApiClient;
  if (!client) return null;
  const sessionId = objectiveArtifactItemId;
  try {
    const [sessionRes, cardRes, questionsRes, controlRes] = await Promise.all([
      client.get<LocalSessionForCard>(`/bridge-sessions/${sessionId}`).catch(() => ({ data: null })),
      client
        .get<BridgeCardItemForAdapter[]>(`/bridge-sessions/${sessionId}/bridge-card`)
        .catch(() => ({ data: [] as BridgeCardItemForAdapter[] })),
      client.get<QuestionForCard[]>(`/bridge-sessions/${sessionId}/questions`, {
        params: { phase: 'current' },
      }).catch(() => ({ data: [] })),
      client.get<{ entropy: Array<{ scope: string; entropy: number }>; metrics?: ControlBridgeMetrics }>(
        `/bridge-sessions/${sessionId}/control-bridge`
      ).catch(() => ({ data: { entropy: [], metrics: undefined } })),
    ]);
    const session = sessionRes.data;
    const items = cardRes.data || [];
    const questions = Array.isArray(questionsRes.data) ? questionsRes.data : [];
    const entropyList = controlRes.data?.entropy || [];
    const firstEntropy = entropyList[0];
    const entropyScore = firstEntropy?.entropy ?? 7;
    const metrics = controlRes.data?.metrics;

    const questionTextById = new Map<string, string>();
    const questionScoreById = new Map<string, number>();
    questions.forEach((q) => {
      if (q.question_id && q.text && !questionTextById.has(q.question_id)) {
        questionTextById.set(q.question_id, q.text);
      }
      if (q.question_id && q.current_answer != null && q.current_answer.score != null) {
        questionScoreById.set(q.question_id, q.current_answer.score);
      }
    });

    const sortedItems = [...(items || [])].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

    const currentStateItems = sortedItems.map(
      (i) => questionTextById.get(i.question_id) || i.reason || `Address question ${i.question_id}`
    );

    const necessaryConditions = sortedItems.map((i) => ({
      condition:
        (i.future_condition && i.future_condition.trim()) ||
        (questionTextById.get(i.question_id)
          ? `Address: ${questionTextById.get(i.question_id)}`
          : i.reason || `Address question ${i.question_id}`),
      change_type: undefined as ChangeType | undefined,
    }));

    const friction_planks = sortedItems.map((i) => ({
      question_text: questionTextById.get(i.question_id) || i.reason || `Question ${i.question_id}`,
      assumption: (i.assumption_text && i.assumption_text.trim()) || '',
      current_entropy_score: questionScoreById.has(i.question_id) ? questionScoreById.get(i.question_id)! : null,
      action: (i.plank_description && i.plank_description.trim()) || '',
      assignee: (i.resource_assignment && i.resource_assignment.trim()) || '',
      duration: (i.estimated_duration && i.estimated_duration.trim()) || '',
      necessary_condition: (i.future_condition && i.future_condition.trim()) || '',
      future_entropy_score: i.future_answer_score != null ? i.future_answer_score : null,
    }));

    const assumptions = sortedItems
      .map((i) => (i.assumption_text && i.assumption_text.trim()) || null)
      .filter((a): a is string => Boolean(a));

    const improvements: (AssumptionChange | ActionImprovement)[] = [];
    sortedItems.forEach((item, conditionIndex) => {
      const hasStructural =
        (item.assumption_text && item.assumption_text.trim()) ||
        (item.future_condition && item.future_condition.trim());
      if (hasStructural) {
        const structuralId = `imp-structural-${item.question_id}-${conditionIndex}`;
        const structural: AssumptionChange = {
          id: structuralId,
          type: 'assumption_change',
          belief_old: (item.assumption_text && item.assumption_text.trim()) || '',
          belief_new: (item.future_condition && item.future_condition.trim()) || '',
          addresses_condition_index: conditionIndex,
        };
        improvements.push(structural);
        if (item.plank_description && item.plank_description.trim()) {
          improvements.push({
            id: `imp-action-${item.question_id}-${conditionIndex}`,
            type: 'action',
            description: item.plank_description.trim(),
            parent_improvement_id: structuralId,
            resource_assignment: (item.resource_assignment && item.resource_assignment.trim()) || undefined,
            estimated_duration: (item.estimated_duration && item.estimated_duration.trim()) || undefined,
          } as ActionImprovement);
        }
      }
    });

    const objectiveText =
      session?.bridge_objective || 'Reduce entropy in the selected area';

    const targetMonths = session?.target_months ?? 0;
    const targetValuation = session?.target_valuation ?? 0;
    const valuePerDay =
      targetMonths > 0 && targetValuation > 0 ? targetValuation / (targetMonths * 30) : undefined;
    const valuePerHour = valuePerDay != null ? valuePerDay / 8 : undefined; // 8-hour working day
    const estimated_time_to_transition =
      targetMonths != null && targetMonths > 0 ? `${targetMonths} months` : undefined;

    return {
      objective_artifact_item_id: sessionId,
      objective_artifact_id: sessionId,
      target_subdomain_artifact_item_id: sessionId,
      target_subdomain_artifact_id: sessionId,
      target_correction_factor: 1,
      current_correction_factor: entropyScore,
      improvement_objective: objectiveText,
      bridge_objective: session?.bridge_objective,
      current_valuation: session?.current_valuation,
      target_valuation: session?.target_valuation,
      target_months: session?.target_months,
      commitment_toll_cost_1hr: session?.commitment_toll_cost_1hr,
      commitment_toll_time: '1 hour session',
      current_revenue: session?.current_revenue ?? metrics?.current_revenue,
      current_expense: session?.current_expense ?? metrics?.current_expense,
      ideal_revenue: metrics?.ideal_revenue,
      ideal_expense: metrics?.ideal_expense,
      recoverable_revenue: metrics?.recoverable_revenue,
      recoverable_expense: metrics?.recoverable_expense,
      value_ratio_current: metrics?.value_ratio_current,
      value_ratio_ideal: metrics?.value_ratio_ideal,
      value_ratio_future: metrics?.value_ratio_future,
      future_revenue: metrics?.future_revenue,
      future_expense: metrics?.future_expense,
      rem_current: metrics?.rem_current,
      eem_current: metrics?.eem_current,
      rem_future: metrics?.rem_future,
      eem_future: metrics?.eem_future,
      estimated_time_to_transition,
      value_per_day: valuePerDay,
      value_per_hour: valuePerHour,
      friction_planks,
      id: sessionId,
      artifact_ref: sessionId,
      artifact_name: 'Bridge objectives',
      artifact_type: 'Objective',
      entropy_score_start: entropyScore,
      entropy_score_current: entropyScore,
      current_state: {
        items: currentStateItems.length ? currentStateItems : ['No high friction priorities yet'],
      },
      assumptions,
      necessary_conditions: necessaryConditions,
      plan: { improvements },
      items_to_change: [] as ChangeItem[],
      status: items.length > 0 ? 'incomplete' : 'incomplete',
      improvement_artifact_item_ids: [],
    };
  } catch {
    return null;
  }
}

export { isLocalBridgeApiEnabled };

/**
 * Fetch product catalog ComponentData from Local Bridge (product_domain_sessions).
 * Used by ProductCatalogComponent when Local Bridge API is enabled.
 */
export async function fetchProductCatalogFromLocalBridge(
  companyId: string
): Promise<import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData | null> {
  try {
    const sessions = await listProductDomainSessions(companyId);
    const namesFromSessions = new Set(
      sessions.map((s) => (s.product_name || '').trim().toLowerCase()).filter(Boolean)
    );

    const productCatalogItems: Array<{
      artifact_id: string;
      artifact_name: string;
      data: {
        product_name: string;
        product_status: string;
        product_revenue: number;
        product_category: undefined;
      };
    }> = sessions.map((s) => ({
      artifact_id: s.id,
      artifact_name: s.product_name || 'Unnamed Product',
      data: {
        product_name: s.product_name || 'Unnamed Product',
        product_status: mapProductStateToStatus(s.product_state),
        product_revenue: s.product_revenue ?? 0,
        product_category: undefined,
      },
    }));

    try {
      const artifacts = await ArtifactService.getArtifacts('company', undefined, undefined, companyId);
      for (const a of artifacts) {
        const typeName = (
          (a as { artifact_type?: string }).artifact_type ||
          a.type ||
          ''
        ).toLowerCase();
        if (typeName !== 'product') continue;
        const nameKey = (a.name || '').trim().toLowerCase();
        if (!nameKey || namesFromSessions.has(nameKey)) continue;
        namesFromSessions.add(nameKey);
        const meta = a.artifact_metadata;
        const statusRaw = (meta?.product_status as string | undefined) || a.status || 'unknown';
        const revenue = typeof meta?.product_revenue === 'number' ? meta.product_revenue : 0;
        productCatalogItems.push({
          artifact_id: a.id,
          artifact_name: a.name,
          data: {
            product_name: a.name,
            product_status: statusRaw,
            product_revenue: revenue,
            product_category: undefined,
          },
        });
      }
    } catch {
      // Catalog still works from sessions alone
    }

    const rowCount = productCatalogItems.length;
    const totalRevenue = productCatalogItems.reduce((sum, row) => sum + (row.data.product_revenue || 0), 0);
    const activeCount = productCatalogItems.filter(
      (row) => (row.data.product_status || '').toLowerCase() === 'active'
    ).length;

    return {
      component_type: 'product_catalog',
      artifact_id: companyId,
      artifact_name: 'Product Catalog',
      prime_artifact_id: null,
      data: {
        products: productCatalogItems.map((row) => ({
          id: row.artifact_id,
          name: row.data.product_name,
          status: row.data.product_status,
          revenue: row.data.product_revenue,
          category: undefined,
        })),
        total_products: rowCount,
        total_revenue: totalRevenue,
        active_products: activeCount,
        categories: [],
      },
      historical_data: [],
      projection_data: [],
      child_data: { product_catalog: productCatalogItems },
      available_properties: ['product_name', 'product_status', 'product_revenue'],
      required_properties: ['product_name', 'product_status'],
    };
  } catch {
    return null;
  }
}

function mapProductStateToStatus(productState: string): string {
  const s = (productState || '').toLowerCase();
  if (s === 'active') return 'Active';
  if (s === 'development') return 'Development';
  return s || 'Unknown';
}

/** Subdomain entropy for BusinessDomainDashboard */
export interface BusinessSubdomainEntropy {
  subdomainId: string;
  subdomainName: string;
  entropyScore: number;
  status: string;
}

/**
 * Fetch dashboard component data for a product domain session.
 * Synthesizes ComponentData from control-bridge and financial summary.
 * Used when ProductDashboard views a product_domain_session (Local Bridge mode).
 */
export async function fetchProductDomainDashboardComponent(
  sessionId: string,
  componentType: 'product_operations' | 'product_demand' | 'product_supply' | 'product_delivery'
): Promise<import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData | null> {
  try {
    const [control, financial, session] = await Promise.all([
      getProductDomainControlBridge(sessionId),
      getProductDomainFinancialSummary(sessionId),
      getProductDomainSession(sessionId),
    ]);
    const revAgg = (financial?.financial_summary?.derived_from as { revenue_aggregates?: Array<{ total_revenue?: number }> })?.revenue_aggregates?.[0];
    const expAgg = (financial?.financial_summary?.derived_from as { expense_aggregates?: Array<{ total_expense?: number }> })?.expense_aggregates?.[0];
    const rev = session?.product_revenue ?? revAgg?.total_revenue ?? 0;
    const exp = session?.product_expense ?? expAgg?.total_expense ?? 0;
    const valueRatio = rev > 0 && exp > 0 ? rev / exp : 0;
    const subdomains = control?.entropy ?? [];
    const getEntropy = (name: string) => {
      const e = subdomains.find((s) => s.scope?.toLowerCase() === name.toLowerCase());
      return e?.entropy ?? 6;
    };
    const demandEntropy = getEntropy('Demand');
    const supplyEntropy = getEntropy('Supply');
    const deliveryEntropy = getEntropy('Delivery');
    const scale = rev > 0 ? Math.max(50, Math.min(500, rev / 200)) : 100;
    const baseDemand = { leads: Math.round(scale * 2), prospects: Math.round(scale * 1.2), contracts: Math.round(scale * 0.5), sales: Math.round(scale * 0.3) };
    const baseSupply = { orders: Math.round(scale * 1.5), inTransit: Math.round(scale * 0.3), received: Math.round(scale * 1.2), inStock: Math.round(scale) };
    const baseDelivery = { invoice: Math.round(scale * 0.9), delivered: Math.round(scale * 0.8), installed: Math.round(scale * 0.7) };
    const productName = session?.product_name || 'Product';

    if (componentType === 'product_demand') {
      const pipeline = await getPipeline(sessionId);
      const demandStages = pipeline?.stages?.filter((s) => s.subdomain === 'demand') ?? [];
      const hasPipelineDemand = pipeline?.config && demandStages.length > 0;

      if (!hasPipelineDemand) {
        const funnel = await getDemandFunnel(sessionId);
        const hasFunnelConfig = funnel?.config && funnel.stages.length > 0;
        if (hasFunnelConfig && funnel) {
          const sortedStages = [...funnel.stages].sort(
            (a, b) => a.display_order - b.display_order
          );
          const funnel_steps = sortedStages.map((s) => ({
            id: s.id,
            name: s.name,
            count: funnel.counts[s.id] ?? 0,
            color: s.color ?? '#3b82f6',
          }));
          const firstCount = funnel_steps[0]?.count ?? 0;
          const lastCount = funnel_steps[funnel_steps.length - 1]?.count ?? 0;
          const overallConversion =
            firstCount > 0 ? Math.round((lastCount / firstCount) * 100) : 0;
          return {
            component_type: 'product_demand',
            artifact_id: sessionId,
            artifact_name: productName,
            prime_artifact_id: null,
            data: {
              funnel_steps,
              customer_leads: funnel_steps[0]?.count ?? 0,
              total_leads: funnel_steps[0]?.count ?? 0,
              customer_prospects: funnel_steps[1]?.count ?? 0,
              total_prospects: funnel_steps[1]?.count ?? 0,
              customer_contracts: funnel_steps[2]?.count ?? 0,
              total_contracts: funnel_steps[2]?.count ?? 0,
              customer_sales: funnel_steps[funnel_steps.length - 1]?.count ?? 0,
              total_sales: funnel_steps[funnel_steps.length - 1]?.count ?? 0,
              CAC: exp > 0 && lastCount > 0 ? Math.round(exp / lastCount) : 0,
              lead_time: 14,
              conversion_rate:
                firstCount > 0 ? overallConversion.toFixed(1) : '0',
              demand_entropy_score: demandEntropy,
            },
            historical_data: [],
            projection_data: [],
            child_data: {},
            available_properties: [
              'funnel_steps',
              'customer_leads',
              'customer_prospects',
              'customer_contracts',
              'customer_sales',
            ],
            required_properties: ['funnel_steps'],
          };
        }
      } else if (pipeline) {
        const sortedStages = [...demandStages].sort(
          (a, b) => a.display_order - b.display_order
        );
        const funnel_steps = sortedStages.map((s) => ({
          id: s.id,
          name: s.name,
          count: pipeline.counts[s.id] ?? 0,
          color: s.color ?? '#3b82f6',
        }));
        const firstCount = funnel_steps[0]?.count ?? 0;
        const lastCount = funnel_steps[funnel_steps.length - 1]?.count ?? 0;
        const overallConversion =
          firstCount > 0 ? Math.round((lastCount / firstCount) * 100) : 0;
        return {
          component_type: 'product_demand',
          artifact_id: sessionId,
          artifact_name: productName,
          prime_artifact_id: null,
          data: {
            funnel_steps,
            customer_leads: funnel_steps[0]?.count ?? 0,
            total_leads: funnel_steps[0]?.count ?? 0,
            customer_prospects: funnel_steps[1]?.count ?? 0,
            total_prospects: funnel_steps[1]?.count ?? 0,
            customer_contracts: funnel_steps[2]?.count ?? 0,
            total_contracts: funnel_steps[2]?.count ?? 0,
            customer_sales: funnel_steps[funnel_steps.length - 1]?.count ?? 0,
            total_sales: funnel_steps[funnel_steps.length - 1]?.count ?? 0,
            CAC: exp > 0 && lastCount > 0 ? Math.round(exp / lastCount) : 0,
            lead_time: 14,
            conversion_rate:
              firstCount > 0 ? overallConversion.toFixed(1) : '0',
            demand_entropy_score: demandEntropy,
          },
          historical_data: [],
          projection_data: [],
          child_data: {},
          available_properties: [
            'funnel_steps',
            'customer_leads',
            'customer_prospects',
            'customer_contracts',
            'customer_sales',
          ],
          required_properties: ['funnel_steps'],
        };
      }

      return {
        component_type: 'product_demand',
        artifact_id: sessionId,
        artifact_name: productName,
        prime_artifact_id: null,
        data: {
          customer_leads: baseDemand.leads,
          total_leads: baseDemand.leads,
          customer_prospects: baseDemand.prospects,
          total_prospects: baseDemand.prospects,
          customer_contracts: baseDemand.contracts,
          total_contracts: baseDemand.contracts,
          customer_sales: baseDemand.sales,
          total_sales: baseDemand.sales,
          CAC: exp > 0 ? Math.round(exp / baseDemand.sales) : 0,
          lead_time: 14,
          conversion_rate: baseDemand.leads > 0 ? ((baseDemand.sales / baseDemand.leads) * 100).toFixed(1) : '0',
          demand_entropy_score: demandEntropy,
        },
        historical_data: [],
        projection_data: [],
        child_data: {},
        available_properties: ['customer_leads', 'customer_prospects', 'customer_contracts', 'customer_sales'],
        required_properties: ['customer_leads', 'customer_prospects'],
      };
    }
    if (componentType === 'product_supply') {
      const pipeline = await getPipeline(sessionId);
      const supplyStages = pipeline?.stages?.filter((s) => s.subdomain === 'supply') ?? [];
      const hasPipelineSupply = pipeline?.config && supplyStages.length > 0;

      if (hasPipelineSupply && pipeline) {
        const sortedStages = [...supplyStages].sort(
          (a, b) => a.display_order - b.display_order
        );
        const supply_steps = sortedStages.map((s, i) => ({
          id: s.id,
          name: s.name,
          count: pipeline.counts[s.id] ?? 0,
          avg_time_hours: i * 24,
        }));
        const firstCount = supply_steps[0]?.count ?? 0;
        return {
          component_type: 'product_supply',
          artifact_id: sessionId,
          artifact_name: productName,
          prime_artifact_id: null,
          data: {
            product_orders: firstCount,
            total_orders: firstCount,
            supply_steps,
            in_transit: supply_steps[1]?.count ?? 0,
            supply_in_transit: supply_steps[1]?.count ?? 0,
            received: supply_steps[2]?.count ?? 0,
            supply_received: supply_steps[2]?.count ?? 0,
            in_stock: supply_steps[3]?.count ?? supply_steps[supply_steps.length - 1]?.count ?? 0,
            inventory_level: supply_steps[supply_steps.length - 1]?.count ?? 0,
            lead_time: 5,
            COGS: exp > 0 && firstCount > 0 ? Math.round(exp / firstCount) : 0,
            avg_order_value: rev > 0 && firstCount > 0 ? Math.round(rev / firstCount) : 0,
            fulfillment_rate: 0.9,
            pending_orders: Math.round(firstCount * 0.2),
            completed_orders: Math.round(firstCount * 0.8),
            supply_entropy_score: supplyEntropy,
          },
          historical_data: [],
          projection_data: [],
          child_data: {},
          available_properties: ['product_orders', 'lead_time', 'COGS'],
          required_properties: ['product_orders', 'COGS'],
        };
      }

      return {
        component_type: 'product_supply',
        artifact_id: sessionId,
        artifact_name: productName,
        prime_artifact_id: null,
        data: {
          product_orders: baseSupply.orders,
          supply_steps: [
            { id: 'order-received', name: 'Order Received', count: baseSupply.orders, avg_time_hours: 0 },
            { id: 'in-transit', name: 'In Transit', count: baseSupply.inTransit, avg_time_hours: 24 },
            { id: 'received', name: 'Received', count: baseSupply.received, avg_time_hours: 48 },
            { id: 'in-stock', name: 'In Stock', count: baseSupply.inStock, avg_time_hours: 72 },
          ],
          lead_time: 5,
          COGS: exp > 0 ? Math.round(exp / baseSupply.orders) : 0,
          avg_order_value: rev > 0 && baseSupply.orders > 0 ? Math.round(rev / baseSupply.orders) : 0,
          fulfillment_rate: 0.9,
          pending_orders: Math.round(baseSupply.orders * 0.2),
          completed_orders: Math.round(baseSupply.orders * 0.8),
          supply_entropy_score: supplyEntropy,
        },
        historical_data: [],
        projection_data: [],
        child_data: {},
        available_properties: ['product_orders', 'lead_time', 'COGS'],
        required_properties: ['product_orders', 'COGS'],
      };
    }
    if (componentType === 'product_delivery') {
      const pipeline = await getPipeline(sessionId);
      const deliveryStages = pipeline?.stages?.filter((s) => s.subdomain === 'delivery') ?? [];
      const hasPipelineDelivery = pipeline?.config && deliveryStages.length > 0;

      if (hasPipelineDelivery && pipeline) {
        const sortedStages = [...deliveryStages].sort(
          (a, b) => a.display_order - b.display_order
        );
        const delivery_steps = sortedStages.map((s) => ({
          id: s.id,
          name: s.name,
          count: pipeline.counts[s.id] ?? 0,
          color: s.color ?? '#8b5cf6',
        }));
        const delivered = delivery_steps[1]?.count ?? delivery_steps[0]?.count ?? 0;
        const installed = delivery_steps[delivery_steps.length - 1]?.count ?? 0;
        return {
          component_type: 'product_delivery',
          artifact_id: sessionId,
          artifact_name: productName,
          prime_artifact_id: null,
          data: {
            product_invoice: delivery_steps[0]?.count ?? 0,
            invoiced: delivery_steps[0]?.count ?? 0,
            products_delivered: delivered,
            delivered,
            products_installed: installed,
            installed,
            delivery_steps,
            payment_received: Math.round(installed * 0.9),
            active_customers: Math.round(installed * 1.2),
            product_support_tickets: 5,
            product_returns: 2,
            avg_delivery_time_days: 3,
            customer_satisfaction: 0.85,
            on_time_delivery_rate: 0.92,
            delivery_entropy_score: deliveryEntropy,
          },
          historical_data: [],
          projection_data: [],
          child_data: {},
          available_properties: ['product_invoice', 'products_delivered', 'products_installed', 'active_customers'],
          required_properties: ['products_delivered', 'active_customers'],
        };
      }

      return {
        component_type: 'product_delivery',
        artifact_id: sessionId,
        artifact_name: productName,
        prime_artifact_id: null,
        data: {
          product_invoice: baseDelivery.invoice,
          invoiced: baseDelivery.invoice,
          products_delivered: baseDelivery.delivered,
          delivered: baseDelivery.delivered,
          products_installed: baseDelivery.installed,
          installed: baseDelivery.installed,
          payment_received: Math.round(baseDelivery.installed * 0.9),
          active_customers: Math.round(baseDelivery.installed * 1.2),
          product_support_tickets: 5,
          product_returns: 2,
          avg_delivery_time_days: 3,
          customer_satisfaction: 0.85,
          on_time_delivery_rate: 0.92,
          delivery_entropy_score: deliveryEntropy,
        },
        historical_data: [],
        projection_data: [],
        child_data: {},
        available_properties: ['product_invoice', 'products_delivered', 'products_installed', 'active_customers'],
        required_properties: ['products_delivered', 'active_customers'],
      };
    }
    if (componentType === 'product_operations') {
      return {
        component_type: 'product_operations',
        artifact_id: sessionId,
        artifact_name: productName,
        prime_artifact_id: null,
        data: {
          customer_leads: baseDemand.leads,
          total_leads: baseDemand.leads,
          customer_prospects: baseDemand.prospects,
          total_prospects: baseDemand.prospects,
          customer_contracts: baseDemand.contracts,
          total_contracts: baseDemand.contracts,
          customer_sales: baseDemand.sales,
          total_sales: baseDemand.sales,
          product_orders: baseSupply.orders,
          total_orders: baseSupply.orders,
          in_transit: baseSupply.inTransit,
          supply_in_transit: baseSupply.inTransit,
          received: baseSupply.received,
          supply_received: baseSupply.received,
          in_stock: baseSupply.inStock,
          inventory_level: baseSupply.inStock,
          product_invoice: baseDelivery.invoice,
          invoiced: baseDelivery.invoice,
          products_delivered: baseDelivery.delivered,
          delivered: baseDelivery.delivered,
          products_installed: baseDelivery.installed,
          installed: baseDelivery.installed,
          cac: exp > 0 ? Math.round(exp / baseDemand.sales) : 0,
          cogs: exp > 0 ? Math.round(exp / baseSupply.orders) : 0,
          demand_lead_time: 14,
          lead_time: 14,
          supply_lead_time: 5,
          active_customers: Math.round(baseDelivery.installed * 1.2),
          product_support_tickets: 5,
          product_returns: 2,
          value_ratio: valueRatio,
        },
        historical_data: [],
        projection_data: [],
        child_data: {},
        available_properties: ['customer_leads', 'customer_prospects', 'product_orders', 'products_delivered'],
        required_properties: [],
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch Product Development ComponentData from Local Bridge (bridge card items as objectives).
 * Maps bridge_card_items to releases/objectives. Each plank = one timeline item.
 */
export async function fetchProductDevelopmentFromLocalBridge(
  sessionId: string
): Promise<import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData | null> {
  try {
    const [control, session] = await Promise.all([
      getProductDomainControlBridge(sessionId),
      getProductDomainSession(sessionId),
    ]);
    const items = control?.bridge_card_items ?? [];
    const productName = session?.product_name || 'Product';

    type BridgeCardItemWithDates = (typeof items)[number] & { start_date?: string; end_date?: string };
    const releases = items.map((item, idx) => {
      const it = item as BridgeCardItemWithDates;
      const name = it.plank_description?.trim() || it.reason?.trim() || it.future_condition?.trim() || `Objective ${idx + 1}`;
      const hasEndDate = Boolean(it.end_date);
      const hasStartDate = Boolean(it.start_date);
      const status: 'completed' | 'in_progress' | 'planned' | 'blocked' = hasEndDate
        ? 'completed'
        : hasStartDate
          ? 'in_progress'
          : 'planned';
      const targetDate = it.end_date || it.start_date || new Date().toISOString().split('T')[0];
      const progress = status === 'completed' ? 100 : status === 'in_progress' ? 50 : 0;
      return {
        id: item.id,
        name,
        status,
        targetDate,
        completedDate: status === 'completed' ? it.end_date : undefined,
        progress,
        items: [
          {
            id: `${item.id}-item`,
            name,
            status,
            priority: item.priority <= 2 ? 'high' : item.priority <= 4 ? 'medium' : 'low',
            progress,
          },
        ],
      };
    });

    const completed = releases.filter((r) => r.status === 'completed').length;
    const inProgress = releases.filter((r) => r.status === 'in_progress').length;
    const planned = releases.filter((r) => r.status === 'planned').length;
    const totalItems = releases.reduce((sum, r) => sum + r.items.length, 0);

    return {
      component_type: 'product_development',
      artifact_id: sessionId,
      artifact_name: productName,
      prime_artifact_id: null,
      data: {
        releases,
        summary: {
          total_releases: releases.length,
          completed,
          in_progress: inProgress,
          planned,
          total_items: totalItems,
          completed_items: releases.reduce((s, r) => s + r.items.filter((i) => i.status === 'completed').length, 0),
        },
      },
      historical_data: [],
      projection_data: [],
      child_data: {},
      available_properties: ['releases', 'summary'],
      required_properties: ['releases'],
    };
  } catch {
    return null;
  }
}

/**
 * Fetch Product Projections ComponentData from Local Bridge (financial summary).
 * Builds historical_data and projection_data from revenue/expense aggregates.
 */
export async function fetchProductProjectionsFromLocalBridge(
  sessionId: string
): Promise<import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData | null> {
  try {
    const [financial, session] = await Promise.all([
      getProductDomainFinancialSummary(sessionId),
      getProductDomainSession(sessionId),
    ]);
    const summary = financial?.financial_summary;
    const derived = summary?.derived_from as {
      revenue_aggregates?: Array<{ reporting_period_id?: string; total_revenue?: number }>;
      expense_aggregates?: Array<{ reporting_period_id?: string; total_expense?: number; cogs_total?: number }>;
    } | undefined;
    const revAggs = derived?.revenue_aggregates ?? [];
    const expAggs = derived?.expense_aggregates ?? [];
    const productName = session?.product_name || 'Product';

    const rev = session?.product_revenue ?? revAggs[0]?.total_revenue ?? 0;
    const exp = session?.product_expense ?? expAggs[0]?.total_expense ?? 0;
    const cogs = expAggs[0]?.cogs_total ?? exp;
    const margin = rev > 0 ? ((rev - cogs) / rev) * 100 : 0;
    const growthRate = (summary?.growth_rate ?? 0) * 100;

    const historical_data: import('../components/Dashboard/ReportingComponents/BaseReportingComponent').PeriodData[] = revAggs.slice(0, 6).map((r, i) => {
      const periodId = r.reporting_period_id ?? `period-${i}`;
      const revenue = r.total_revenue ?? 0;
      const expRow = expAggs.find((e) => e.reporting_period_id === periodId) ?? expAggs[i];
      const expense = expRow?.total_expense ?? 0;
      const periodCogs = expRow?.cogs_total ?? expense;
      return {
        item_id: periodId,
        item_name: `Period ${i + 1}`,
        start_date: null,
        end_date: null,
        is_projected: false,
        values: {
          revenue,
          cogs: periodCogs,
          margin: revenue > 0 ? ((revenue - periodCogs) / revenue) * 100 : 0,
        },
      };
    });

    const projectionCount = 4;
    const projection_data: import('../components/Dashboard/ReportingComponents/BaseReportingComponent').PeriodData[] = [];
    const lastRev = revAggs[0]?.total_revenue ?? rev;
    const lastExp = expAggs[0]?.total_expense ?? exp;
    const growth = 1 + (summary?.growth_rate ?? 0.1);

    for (let i = 0; i < projectionCount; i++) {
      const factor = Math.pow(growth, i + 1);
      const projectedRevenue = Math.round(lastRev * factor);
      const projectedCogs = Math.round(lastExp * factor * 0.9);
      const uncertainty = 0.1 + i * 0.05;
      projection_data.push({
        item_id: `proj-${i}`,
        item_name: `Projected ${i + 1}`,
        start_date: null,
        end_date: null,
        is_projected: true,
        values: {
          revenue: projectedRevenue,
          revenue_low: Math.round(projectedRevenue * (1 - uncertainty)),
          revenue_high: Math.round(projectedRevenue * (1 + uncertainty)),
          cogs: projectedCogs,
          cogs_low: Math.round(projectedCogs * (1 - uncertainty * 0.7)),
          cogs_high: Math.round(projectedCogs * (1 + uncertainty * 0.7)),
          margin: projectedRevenue > 0 ? ((projectedRevenue - projectedCogs) / projectedRevenue) * 100 : 0,
        },
      });
    }

    return {
      component_type: 'product_projections',
      artifact_id: sessionId,
      artifact_name: productName,
      prime_artifact_id: null,
      data: {
        revenue: rev,
        cogs,
        margin,
        summary: {
          total_projected_revenue: projection_data.reduce((s, p) => s + (p.values.revenue ?? 0), 0),
          total_projected_cogs: projection_data.reduce((s, p) => s + (p.values.cogs ?? 0), 0),
          average_margin: margin,
          growth_rate: growthRate,
        },
        assumptions: [
          { name: 'Revenue Growth Rate', value: `${growthRate.toFixed(1)}%`, type: 'growth' as const },
          { name: 'Resource Cost', value: `$${cogs.toLocaleString()}`, type: 'resource' as const },
        ],
      },
      historical_data,
      projection_data,
      child_data: {},
      available_properties: ['revenue', 'cogs', 'margin', 'assumptions'],
      required_properties: ['revenue', 'cogs'],
    };
  } catch {
    return null;
  }
}

/**
 * Fetch MarketFit ComponentData from Local Bridge (deals, situations, products).
 * Company-scoped; product_id is session id - resolve company_id from session.
 */
export async function fetchMarketFitFromLocalBridge(
  companyId: string,
  productSessionId?: string
): Promise<import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData | null> {
  try {
    const [deals, situations, products] = await Promise.all([
      listMarketFitDeals(companyId),
      listMarketFitSituations(companyId),
      listMarketFitProducts(companyId),
    ]);

    const dealList = deals.map((d) => {
      const score = (d as { marketfit_score?: number; deal_score?: number }).marketfit_score ?? d.deal_score ?? 0;
      const state = (d.state ?? '').toUpperCase();
      const status: 'active' | 'pending' | 'won' | 'lost' =
        state === 'CLOSED' ? 'won' : state === 'ACTIVE' ? 'active' : 'pending';
      const value = d.product_situation_value ?? 0;
      return {
        id: d.id,
        name: d.name,
        score: Math.round(score),
        status,
        value,
        situationId: d.situation_ids?.[0],
        solutionId: d.product_ids?.[0],
      };
    });

    const situationList = situations.map((s) => {
      const situationDeals = deals.filter((d) => d.situation_ids?.includes(s.id));
      const dealCount = situationDeals.length;
      const productCount = new Set(situationDeals.flatMap((d) => d.product_ids ?? [])).size;
      return {
        id: s.id,
        name: s.situation_name ?? s.name,
        description: s.description,
        productCount,
        dealCount,
      };
    });

    const productDealCount = (productId: string) =>
      deals.filter((d) => d.product_ids?.includes(productId)).length;
    const dealScoresForProduct = (productId: string) =>
      deals.filter((d) => d.product_ids?.includes(productId)).map((d) => (d as { marketfit_score?: number }).marketfit_score ?? d.deal_score ?? 0);
    const solutions = products.map((p) => {
      const scores = dealScoresForProduct(p.id).filter((s) => s > 0);
      const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      return {
        id: p.id,
        name: p.name,
        description: p.description,
        fitScore: Math.round(avgScore) || 70,
        dealCount: productDealCount(p.id),
      };
    });

    const activeDeals = dealList.filter((d) => d.status === 'active');
    const wonDeals = dealList.filter((d) => d.status === 'won');
    const avgScore = dealList.length > 0
      ? Math.round(dealList.reduce((s, d) => s + d.score, 0) / dealList.length)
      : 0;
    const pipelineValue = dealList
      .filter((d) => (['active', 'pending', 'won'] as const).includes(d.status))
      .reduce((s, d) => s + d.value, 0);

    return {
      component_type: 'market_fit',
      artifact_id: productSessionId ?? companyId,
      artifact_name: 'MarketFit',
      prime_artifact_id: null,
      data: {
        deals: dealList,
        situations: situationList,
        solutions,
        summary: {
          total_deals: dealList.length,
          active_deals: activeDeals.length,
          average_score: avgScore,
          total_pipeline_value: pipelineValue,
          won_deals: wonDeals.length,
        },
      },
      historical_data: [],
      projection_data: [],
      child_data: {},
      available_properties: ['deals', 'situations', 'solutions'],
      required_properties: ['deals'],
    };
  } catch {
    return null;
  }
}

/**
 * Summary metrics for Product Dashboard cards.
 * Fetches all four views in parallel for efficient display.
 */
export interface ProductDashboardSummary {
  productName: string;
  companyId: string | null;
  operations: { demand: string; supply: string; delivery: string };
  development: { releases: string; inProgress: string; planned: string };
  projections: { revenue: string; cogs: string; margin: string };
  marketFit: { deals: string; avgScore: string; active: string };
}

export async function fetchProductDashboardSummary(
  productId: string
): Promise<ProductDashboardSummary | null> {
  try {
    const session = await getProductDomainSession(productId);
    const productName = session?.product_name || 'Product';
    const companyId = session?.company_id ?? null;

    if (!isLocalBridgeApiEnabled()) {
      return { productName, companyId, operations: { demand: '—', supply: '—', delivery: '—' }, development: { releases: '—', inProgress: '—', planned: '—' }, projections: { revenue: '—', cogs: '—', margin: '—' }, marketFit: { deals: '—', avgScore: '—', active: '—' } };
    }

    const [control, devData, projData, mfData] = await Promise.all([
      getProductDomainControlBridge(productId),
      fetchProductDevelopmentFromLocalBridge(productId),
      fetchProductProjectionsFromLocalBridge(productId),
      companyId ? fetchMarketFitFromLocalBridge(companyId, productId) : Promise.resolve(null),
    ]);

    const getEntropy = (name: string) => {
      const e = control?.entropy?.find((s) => s.scope?.toLowerCase() === name.toLowerCase());
      return e != null ? String(Math.round(e.entropy)) : '—';
    };

    const dev = devData?.data ?? {};
    const devSummary = dev.summary ?? {};

    const proj = projData?.data ?? {};
    const formatCur = (n: number) => (n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : n > 0 ? `$${n}` : '—');

    const mf = mfData?.data ?? {};
    const mfSummary = mf.summary ?? {};

    return {
      productName,
      companyId,
      operations: {
        demand: getEntropy('demand'),
        supply: getEntropy('supply'),
        delivery: getEntropy('delivery'),
      },
      development: {
        releases: String(devSummary.total_releases ?? '—'),
        inProgress: String(devSummary.in_progress ?? '—'),
        planned: String(devSummary.planned ?? '—'),
      },
      projections: {
        revenue: formatCur(proj.revenue ?? 0),
        cogs: formatCur(proj.cogs ?? 0),
        margin: proj.margin != null ? `${proj.margin.toFixed(0)}%` : '—',
      },
      marketFit: {
        deals: String(mfSummary.total_deals ?? '—'),
        avgScore: mfSummary.average_score != null ? `${mfSummary.average_score}` : '—',
        active: String(mfSummary.active_deals ?? '—'),
      },
    };
  } catch {
    return null;
  }
}

/** Minimal customer summary for CustomersDashboard when Local Bridge enabled */
export interface CustomerSummaryFromLocalBridge {
  customer_value_ratio: number;
  active_customers: number | null;
  market_size: number | null;
  channels_count: number | null;
  growth_rate: number | null;
}

/**
 * Fetch minimal customer summary from Local Bridge for CustomersDashboard.
 * Derives from latest bridge session control-bridge (value_ratio_ideal) and product domain sessions.
 */
export async function fetchCustomerSummaryFromLocalBridge(
  companyId: string
): Promise<CustomerSummaryFromLocalBridge | null> {
  try {
    const result = await fetchControlBridgeForCompany(companyId);
    const m = result.componentData.data;
    const valueRatio = m.customer_value_ratio ?? m.business_value_ratio ?? 0;
    const productsCount = result.metadata?.productsCount ?? 0;
    return {
      customer_value_ratio: valueRatio,
      active_customers: productsCount > 0 ? null : null,
      market_size: null,
      channels_count: null,
      growth_rate: null,
    };
  } catch {
    return null;
  }
}

/** Subdomain id (lowercase) -> domain_artifact (from bridge card items) */
export const SUBDOMAIN_TO_DOMAIN_ARTIFACT: Record<string, string> = {
  strategy: 'Strategy',
  finance: 'Finance',
  operations: 'Operations',
  revenue: 'Revenue',
  resources: 'Resource',
};

export interface BusinessDomainActivity {
  id: string;
  name: string;
  priority: number;
  domain_artifact?: string;
  resource_name?: string;
  start_date?: string;
  end_date?: string;
  duration?: string;
  remaining_duration?: string;
  plank_description?: string;
}

export interface BusinessDomainDetail {
  subdomainEntropy: BusinessSubdomainEntropy[] | null;
  activities: BusinessDomainActivity[];
}

/**
 * Fetch business domain detail: subdomain entropy + activities (bridge card items).
 * For BusinessDomainDashboard detail panel.
 */
export async function fetchBusinessDomainDetail(
  companyId: string
): Promise<BusinessDomainDetail> {
  const subdomainEntropy = await fetchBusinessSubdomainEntropy(companyId);
  let activities: BusinessDomainActivity[] = [];

  if (isLocalBridgeApiEnabled() && localBridgeApiClient) {
    try {
      const res = await localBridgeApiClient.get<{
        bridge_card_items?: Array<{
          id: string;
          question_id?: string;
          reason?: string;
          priority?: number;
          question_text?: string;
          domain_artifact?: string;
          resource_name?: string;
          resource_assignment?: string;
          start_date?: string;
          end_date?: string;
          duration?: string;
          remaining_duration?: string;
          plank_description?: string;
        }>;
      }>(`/companies/${companyId}/control-bridge`);
      const items = res.data?.bridge_card_items ?? [];
      activities = items.map((item, index) => ({
        id: item.id,
        name: item.question_text ?? item.reason ?? item.plank_description ?? `Activity ${index + 1}`,
        priority: item.priority ?? index + 1,
        domain_artifact: item.domain_artifact,
        resource_name: item.resource_name ?? item.resource_assignment,
        start_date: item.start_date,
        end_date: item.end_date,
        duration: item.duration,
        remaining_duration: item.remaining_duration,
        plank_description: item.plank_description,
      }));
    } catch {
      activities = [];
    }
  }

  return { subdomainEntropy, activities };
}

/**
 * Fetch business subdomain entropy from Local Bridge for BusinessDomainDashboard.
 * Maps control-bridge entropy (factors/domains) to subdomains.
 */
export async function fetchBusinessSubdomainEntropy(
  companyId: string
): Promise<BusinessSubdomainEntropy[] | null> {
  try {
    const [sessions, company] = await Promise.all([listBridgeSessions(companyId), getCompany(companyId)]);
    const sessionIds = new Set(sessions.map((s) => s.id));
    const preferredId = company?.control_bridge_session_id;
    const preferred = preferredId && sessionIds.has(preferredId) ? sessions.find((s) => s.id === preferredId) : undefined;
    const latest = (preferred ?? sessions[0]) as { id: string } | undefined;
    if (!latest) return null;
    const control = await fetchControlBridgeFromLocalApi(latest.id);
    if (!control?.entropy?.length) return null;
    const scopeMap = new Map<string, number>();
    control.entropy.forEach((e) => scopeMap.set(e.scope, e.entropy));
    const subdomains = ['Strategy', 'Finance', 'Operations', 'Revenue', 'Resources'] as const;
    const result: BusinessSubdomainEntropy[] = [];
    for (const name of subdomains) {
      let score = scopeMap.get(name) ?? scopeMap.get(name === 'Resources' ? 'Resource' : name);
      if (score == null) {
        const factors = Object.entries(ENTROPY_FACTOR_TO_SUBDOMAINS)
          .filter(([, subs]) => subs.includes(name))
          .map(([f]) => f);
        const factorScores = factors.map((f) => scopeMap.get(f)).filter((s): s is number => s != null);
        score = factorScores.length > 0 ? Math.max(...factorScores) : 8;
      }
      const status = score <= 3 ? 'Healthy' : score <= 6 ? 'Active' : score <= 8 ? 'Needs Attention' : 'High Entropy';
      result.push({
        subdomainId: name.toLowerCase(),
        subdomainName: name,
        entropyScore: Math.round(score),
        status,
      });
    }
    return result;
  } catch {
    return null;
  }
}

/** Local API control-bridge response shape */
export interface LocalControlBridgeResponse {
  session_id: string;
  /** How entropy was derived: partial_answers, answers, cache, or default placeholder */
  entropy_source?: 'partial_answers' | 'answers' | 'cache' | 'default';
  metrics: {
    value_ratio_current: number;
    value_ratio_ideal: number;
    value_ratio_future: number;
    recoverable_revenue: number;
    recoverable_expense: number;
    current_revenue?: number;
    current_expense?: number;
    ideal_revenue?: number;
    ideal_expense?: number;
    future_revenue?: number;
    future_expense?: number;
  };
  entropy: Array<{ scope: string; entropy: number; correction: number }>;
  bridge_card_items: Array<{
    id: string;
    question_id: string;
    reason?: string;
    priority: number;
    question_text?: string;
    artifact_being_changed?: string;
    resource_id?: string;
    resource_name?: string;
    domain_artifact?: string;
    bridge_card_id?: string;
    bridge_card_item_id?: string;
    start_date?: string;
    end_date?: string;
    duration?: string;
    remaining_duration?: string;
    plank_description?: string;
    resource_assignment?: string;
    estimated_duration?: string;
    future_condition?: string | null;
    assumption_text?: string | null;
    future_answer_score?: number | null;
  }>;
}

/** Product item for Control Bridge company view */
export interface ControlBridgeProductItem {
  id: string;
  name: string;
  value_ratio: number;
  entropy_score: number;
  status: string;
}

/**
 * Map bridge_card_items to business_priorities format for DomainDetailSection.
 */
function mapBridgeCardItemsToBusinessPriorities(
  items: LocalControlBridgeResponse['bridge_card_items']
): Array<{
  id: string;
  name: string;
  sequence: number;
  enabled: boolean;
  type: 'improvement' | 'operational';
  domain_artifact?: string;
  resource_name?: string;
  start_date?: string;
  end_date?: string;
  duration?: string;
  remaining_duration?: string;
}> {
  return (items ?? []).map((item, index) => {
    const question = item.question_text ?? item.reason ?? `Priority ${index + 1}`;
    const action = item.plank_description?.trim();
    const name = action ? `${question} → ${action}` : question;
    return {
      id: item.id,
      name,
      sequence: item.priority ?? index + 1,
      enabled: true,
      type: 'improvement' as const,
      domain_artifact: item.domain_artifact,
      resource_name: item.resource_name ?? item.resource_assignment,
      start_date: item.start_date,
      end_date: item.end_date,
      duration: item.duration ?? item.estimated_duration,
      remaining_duration: item.remaining_duration,
    };
  });
}

/**
 * Synthesize historical_data and projection_data from control-bridge metrics
 * so ValueBreakdownChart renders (single current period + one projection).
 */
function synthesizePeriodDataFromMetrics(m: LocalControlBridgeResponse['metrics']): {
  historical_data: import('../components/Dashboard/ReportingComponents/BaseReportingComponent').PeriodData[];
  projection_data: import('../components/Dashboard/ReportingComponents/BaseReportingComponent').PeriodData[];
} {
  const now = new Date();
  const currentMonth = now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextMonthStr = nextMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const curRev = m.current_revenue ?? 0;
  const curExp = m.current_expense ?? 0;
  const idealRev = m.ideal_revenue ?? curRev;
  const idealExp = m.ideal_expense ?? curExp;
  const futureRev = m.future_revenue ?? idealRev;
  const futureExp = m.future_expense ?? idealExp;
  const stakeholderValue = curRev;
  const stakeholderCost = curExp;
  const productValue = futureRev;
  const productCost = futureExp;
  const uncertainty = 0.15;
  return {
    historical_data: [
      {
        item_id: 'period-current',
        item_name: currentMonth,
        start_date: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
        end_date: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0],
        is_projected: false,
        values: {
          stakeholder_value_generated: stakeholderValue,
          stakeholder_cost_to_generate: stakeholderCost,
          business_value_generated: curRev,
          business_cost_to_generate: curExp,
          customer_value_generated: idealRev,
          customer_cost_to_generate: idealExp,
          product_value_generated: productValue,
          product_cost_to_generate: productCost,
        },
      },
    ],
    projection_data: [
      {
        item_id: 'period-proj-0',
        item_name: nextMonthStr,
        start_date: nextMonth.toISOString().split('T')[0],
        end_date: new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).toISOString().split('T')[0],
        is_projected: true,
        values: {
          stakeholder_value_generated: futureRev,
          stakeholder_value_generated_low: futureRev * (1 - uncertainty),
          stakeholder_value_generated_high: futureRev * (1 + uncertainty),
          stakeholder_cost_to_generate: futureExp,
          business_value_generated: futureRev,
          business_value_generated_low: futureRev * (1 - uncertainty),
          business_value_generated_high: futureRev * (1 + uncertainty),
          business_cost_to_generate: futureExp,
          customer_value_generated: futureRev,
          customer_value_generated_low: futureRev * (1 - uncertainty),
          customer_value_generated_high: futureRev * (1 + uncertainty),
          customer_cost_to_generate: futureExp,
          product_value_generated: futureRev,
          product_value_generated_low: futureRev * (1 - uncertainty),
          product_value_generated_high: futureRev * (1 + uncertainty),
          product_cost_to_generate: futureExp,
        },
      },
    ],
  };
}

/**
 * Map Local Bridge API control-bridge response to ComponentData for BusinessDashboard.
 */
export function mapControlBridgeToComponentData(
  res: LocalControlBridgeResponse,
  artifactId: string,
  products?: ControlBridgeProductItem[]
): import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData {
  const m = res.metrics;
  const businessEntropy = res.entropy.find((e) => e.scope === 'Business');
  const entropyScore = businessEntropy?.entropy ?? 5;
  const businessPriorities = mapBridgeCardItemsToBusinessPriorities(res.bridge_card_items ?? []);
  const { historical_data, projection_data } = synthesizePeriodDataFromMetrics(m);
  return {
    component_type: 'control_bridge',
    artifact_id: artifactId,
    artifact_name: 'Control Bridge',
    prime_artifact_id: null,
    data: {
      stakeholder_value_ratio: m.value_ratio_current,
      stakeholder_entropy_score: entropyScore,
      business_value_ratio: m.value_ratio_current,
      business_entropy_score: entropyScore,
      customer_value_ratio: m.value_ratio_ideal,
      customer_entropy_score: entropyScore,
      product_bridge_value_ratio: m.value_ratio_future,
      product_bridge_entropy_score: entropyScore,
      products: products ?? [],
      stakeholder_priorities: [],
      business_priorities: businessPriorities,
      customer_priorities: [],
      'product-bridge_priorities': [],
      stakeholder_resources: [],
      business_resources: [],
      customer_resources: [],
      'product-bridge_resources': [],
    },
    historical_data,
    projection_data,
    child_data: {},
    available_properties: [],
    required_properties: [],
  };
}

/**
 * Apply state-based entropy override per plan: 10=no artifact, 9=DRAFTED, 8=ACTIVE+no data, 7-1=calculated.
 */
function applyEntropyStateOverride(
  calculated: number | undefined,
  hasArtifact: boolean,
  isDrafted: boolean,
  hasData: boolean
): number {
  if (!hasArtifact) return 10;
  // Partial answers produce real entropy before Calculate; show it instead of draft placeholder (9).
  if (hasData && calculated != null) return Math.round(calculated);
  if (isDrafted) return 9;
  if (!hasData || calculated == null) return 8;
  return Math.round(calculated);
}

/** Plank from product domain bridge card (for priorities section) */
export interface ProductBridgePlank {
  question_text: string;
  action?: string;
  assignee?: string;
  duration?: string;
  future_condition?: string;
}

/** Product bridge card with planks (for Product Development priorities) */
export interface ProductBridgeCardWithPlanks {
  sessionId: string;
  productName: string;
  planks: ProductBridgePlank[];
}

/** Metadata for company control bridge (indicators, objectives) */
export interface ControlBridgeCompanyMetadata {
  hasBusinessObjective: boolean;
  bridgeCardActive: boolean;
  bridgeSessionRecommended: boolean;
  productsCount: number;
  latestSessionId: string | null;
  /** Product domain sessions with bridge_built and planks (for Product Development section) */
  productBridgeCardsWithPlanks?: ProductBridgeCardWithPlanks[];
  /** Business bridge session planks (Priorities → Business) */
  businessBridgeCardsWithPlanks?: ProductBridgeCardWithPlanks[];
}

/**
 * Default Control Bridge dashboard payload when the company has no bridge session and no product
 * domain sessions yet (e.g. new signup). Entropy 10 = no artifact session (applyEntropyStateOverride).
 */
export function buildEmptyControlBridgeForCompany(companyId: string): {
  componentData: import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData;
  metadata: ControlBridgeCompanyMetadata;
} {
  const businessEntropy = applyEntropyStateOverride(undefined, false, false, false);
  const shell: LocalControlBridgeResponse = {
    session_id: '',
    metrics: {
      value_ratio_current: 0,
      value_ratio_ideal: 0,
      value_ratio_future: 0,
      recoverable_revenue: 0,
      recoverable_expense: 0,
    },
    entropy: [{ scope: 'Business', entropy: businessEntropy, correction: 1 }],
    bridge_card_items: [],
  };
  return {
    componentData: mapControlBridgeToComponentData(shell, companyId, []),
    metadata: {
      hasBusinessObjective: false,
      bridgeCardActive: false,
      bridgeSessionRecommended: false,
      productsCount: 0,
      latestSessionId: null,
    },
  };
}

/** List business bridge sessions: Local Bridge client when configured, else main API `/bridge-sessions/bridge-sessions`. */
async function listBridgeSessionsResolved(companyId?: string): Promise<LocalBridgeSession[]> {
  if (isLocalBridgeApiEnabled() && localBridgeApiClient) {
    return listBridgeSessions(companyId);
  }
  try {
    const params = companyId ? { company_id: companyId } : {};
    const res = await apiClient.get<LocalBridgeSession[]>('/bridge-sessions/bridge-sessions', { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

/** Company row with control_bridge_session_id: Local Bridge or CRUD company. */
async function getCompanyResolved(companyId: string): Promise<LocalCompany | null> {
  if (isLocalBridgeApiEnabled() && localBridgeApiClient) {
    return getCompany(companyId);
  }
  try {
    const res = await apiClient.get<{
      id: string;
      name: string;
      created_at?: string;
      control_bridge_session_id?: string | null;
    }>(API_ENDPOINTS.COMPANIES.BY_ID(companyId));
    const d = res.data;
    return {
      id: d.id,
      name: d.name,
      created_at: d.created_at ?? new Date().toISOString(),
      control_bridge_session_id: d.control_bridge_session_id ?? null,
    };
  } catch {
    return null;
  }
}

/** GET /api/v1/companies/{id}/control-bridge — aggregate business session payload (auth). */
async function fetchCompanyAggregateControlBridgeMainApi(
  companyId: string
): Promise<LocalControlBridgeResponse | null> {
  try {
    const { data } = await apiClient.get<LocalControlBridgeResponse>(
      API_ENDPOINTS.COMPANIES.CONTROL_BRIDGE(companyId)
    );
    return data ?? null;
  } catch {
    return null;
  }
}

/** Single bridge session row: Local Bridge client or main API `/bridge-sessions/bridge-sessions/{id}`. */
export async function getBridgeSessionResolved(sessionId: string): Promise<LocalBridgeSession | null> {
  if (isLocalBridgeApiEnabled() && localBridgeApiClient) {
    return getBridgeSession(sessionId);
  }
  try {
    const res = await apiClient.get<LocalBridgeSession>(`/bridge-sessions/bridge-sessions/${sessionId}`);
    return res.data;
  } catch {
    return null;
  }
}

/**
 * Fetch control-bridge data for a company (all sessions + product domain sessions).
 * Always returns ComponentData; on failure or empty company, returns onboarding defaults.
 */
export async function fetchControlBridgeForCompany(
  companyId: string
): Promise<{
  componentData: import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData;
  metadata: ControlBridgeCompanyMetadata;
}> {
  try {
    const [bridgeSessions, productSessions, company] = await Promise.all([
      listBridgeSessionsResolved(companyId),
      listProductDomainSessions(companyId),
      getCompanyResolved(companyId),
    ]);
    const sessionIds = new Set(bridgeSessions.map((s) => s.id));
    const preferredSessionId = company?.control_bridge_session_id;
    const preferredSession =
      preferredSessionId && sessionIds.has(preferredSessionId)
        ? bridgeSessions.find((s) => s.id === preferredSessionId)
        : undefined;
    const latestBridgeSession = (preferredSession ?? bridgeSessions[0]) as
      | { id: string; status?: string; bridge_objective?: string }
      | undefined;
    let businessControlBridge: LocalControlBridgeResponse | null = null;
    if (latestBridgeSession) {
      businessControlBridge = await fetchControlBridgeFromLocalApi(latestBridgeSession.id);
    }
    if (!businessControlBridge) {
      businessControlBridge = await fetchCompanyAggregateControlBridgeMainApi(companyId);
    }

    const DRAFT_STATUSES = ['draft', 'collecting_current', 'collecting_future'];
    const businessIsDraft = latestBridgeSession && DRAFT_STATUSES.includes(latestBridgeSession.status || '');
    const QUESTION_DOMAIN_SCOPES = ['Finance', 'Operations', 'Strategy', 'Revenue', 'Resources'];
    const businessHasData =
      businessControlBridge?.entropy_source === 'partial_answers' ||
      businessControlBridge?.entropy_source === 'answers' ||
      businessControlBridge?.entropy_source === 'cache' ||
      (businessControlBridge?.entropy ?? []).some((e) => QUESTION_DOMAIN_SCOPES.includes(e.scope));
    const businessCalculated = businessControlBridge?.entropy?.find((e) => e.scope === 'Business')?.entropy;

    const products: ControlBridgeProductItem[] = [];
    const productBridgeCardsWithPlanks: ProductBridgeCardWithPlanks[] = [];
    for (const ps of productSessions) {
      const cb = await getProductDomainControlBridge(ps.id);
      const valueRatio = cb?.metrics?.value_ratio_current ?? 0;
      const productCalculated = cb?.entropy?.[0]?.entropy;
      const productIsDraft = DRAFT_STATUSES.includes(ps.status || '') || ps.status === 'draft';
      const productHasData = productCalculated != null && (cb?.entropy?.length ?? 0) > 0;
      const entropyScore = applyEntropyStateOverride(
        productCalculated,
        true,
        productIsDraft,
        productHasData
      );
      products.push({
        id: ps.id,
        name: ps.product_name || 'Unnamed Product',
        value_ratio: valueRatio,
        entropy_score: entropyScore,
        status: ps.status || 'draft',
      });
      if (ps.status === 'bridge_built' && cb?.bridge_card_items?.length) {
        const planks: ProductBridgePlank[] = cb.bridge_card_items
          .filter(
            (i) =>
              (i.plank_description && i.plank_description.trim()) ||
              (i.future_condition && i.future_condition.trim())
          )
          .map((i) => ({
            question_text: i.reason ?? i.question_id,
            action: i.plank_description?.trim() || undefined,
            assignee: i.resource_assignment?.trim() || undefined,
            duration: i.estimated_duration?.trim() || undefined,
            future_condition: i.future_condition?.trim() || undefined,
          }));
        if (planks.length > 0) {
          productBridgeCardsWithPlanks.push({
            sessionId: ps.id,
            productName: ps.product_name || 'Unnamed Product',
            planks,
          });
        }
      }
    }

    const businessEntropy = applyEntropyStateOverride(
      businessCalculated,
      !!latestBridgeSession,
      !!businessIsDraft,
      businessHasData
    );
    const hasBridgeCard = (businessControlBridge?.bridge_card_items?.length ?? 0) > 0;
    const status = (latestBridgeSession?.status || '').toLowerCase();
    // Business flow sets status=calculated after Calculate; bridge_built after Bridge Card.
    // Show card/planks for either so in-progress completions surface on Control Bridge.
    const statusBridgeBuilt = status === 'bridge_built' || status === 'calculated';
    const hasObjective = Boolean(latestBridgeSession?.bridge_objective?.trim());
    // Show "Priorities" when workshop complete (has objective) OR bridge session has results
    const objectivesOrBridgeComplete = hasObjective || statusBridgeBuilt;

    let businessBridgeCardsWithPlanks: ProductBridgeCardWithPlanks[] | undefined;
    if (hasBridgeCard && statusBridgeBuilt && latestBridgeSession && businessControlBridge) {
      const planks: ProductBridgePlank[] = (businessControlBridge.bridge_card_items ?? [])
        .filter(
          (i) =>
            (i.plank_description && i.plank_description.trim()) ||
            (i.future_condition && i.future_condition.trim()) ||
            (i.question_text && i.question_text.trim()) ||
            (i.reason && i.reason.trim())
        )
        .map((i) => ({
          question_text: i.question_text ?? i.reason ?? i.question_id,
          action: i.plank_description?.trim() || undefined,
          assignee:
            i.resource_name?.trim() ||
            i.resource_assignment?.trim() ||
            undefined,
          duration: i.estimated_duration?.trim() || i.duration?.trim() || undefined,
          future_condition: i.future_condition?.trim() || undefined,
        }));
      if (planks.length > 0) {
        businessBridgeCardsWithPlanks = [
          {
            sessionId: latestBridgeSession.id,
            productName: 'Business Bridge Card',
            planks,
          },
        ];
      }
    }

    const metadata: ControlBridgeCompanyMetadata = {
      hasBusinessObjective: objectivesOrBridgeComplete,
      bridgeCardActive: hasBridgeCard && statusBridgeBuilt,
      bridgeSessionRecommended: businessEntropy >= 7,
      productsCount: products.length,
      latestSessionId: latestBridgeSession?.id ?? null,
      productBridgeCardsWithPlanks:
        productBridgeCardsWithPlanks.length > 0 ? productBridgeCardsWithPlanks : undefined,
      businessBridgeCardsWithPlanks,
    };

    const businessEntropyOverridden = businessControlBridge?.entropy?.map((e) =>
      e.scope === 'Business' ? { ...e, entropy: businessEntropy } : e
    ) ?? [];
    const hasBusinessInEntropy = businessEntropyOverridden.some((e) => e.scope === 'Business');
    const businessControlBridgeWithOverride = businessControlBridge
      ? {
          ...businessControlBridge,
          entropy: hasBusinessInEntropy
            ? businessEntropyOverridden
            : [...businessEntropyOverridden, { scope: 'Business', entropy: businessEntropy, correction: 1 }],
        }
      : null;

    let componentData: import('../components/Dashboard/ReportingComponents/BaseReportingComponent').ComponentData;
    if (businessControlBridgeWithOverride) {
      componentData = mapControlBridgeToComponentData(
        businessControlBridgeWithOverride,
        companyId,
        products
      );
    } else if (products.length > 0) {
      componentData = mapControlBridgeToComponentData(
        {
          session_id: '',
          metrics: {
            value_ratio_current: 0,
            value_ratio_ideal: 0,
            value_ratio_future: 0,
            recoverable_revenue: 0,
            recoverable_expense: 0,
          },
          entropy: [{ scope: 'Business', entropy: businessEntropy, correction: 1 }],
          bridge_card_items: [],
        },
        companyId,
        products
      );
    } else {
      return buildEmptyControlBridgeForCompany(companyId);
    }

    return { componentData, metadata };
  } catch (e) {
    console.warn('fetchControlBridgeForCompany: falling back to empty onboarding state', e);
    return buildEmptyControlBridgeForCompany(companyId);
  }
}

/**
 * Fetch control-bridge report from Local Bridge API. Returns null on failure or when disabled.
 */
export async function fetchControlBridgeFromLocalApi(
  sessionId: string
): Promise<LocalControlBridgeResponse | null> {
  const client = localBridgeApiClient;
  try {
    if (client) {
      const res = await client.get<LocalControlBridgeResponse>(
        `/bridge-sessions/${sessionId}/control-bridge`
      );
      return res.data;
    }
    const res = await apiClient.get<LocalControlBridgeResponse>(
      `/bridge-sessions/bridge-sessions/${sessionId}/control-bridge`
    );
    return res.data;
  } catch {
    return null;
  }
}
